import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, EventEmitter, HostListener, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ReviewerService } from '../../core/services/reviewer.service';
import { DateFormatService } from '../../core/services/date-format.service';
import { ReviewLocationCard } from '../review-process.interfaces';
import { GLRR_FIELD_CONFIG, GLRR_TIMELINE_OFFSETS, TARGET_PUB_WORKING_DAYS_MULTI, TARGET_PUB_WORKING_DAYS_SINGLE, TIMELINE_OFFSETS } from './schedule.constants';

export interface ScheduleForm {
  parentChild: string;
  parentReviewId: string;
  serviceLines: string[];
  fieldworkStartDate: string;
  fieldworkEndDate: string;
  goLiveDate: string;
  preGoLiveFWStartDate: string;
  preGoLiveFWEndDate: string;
  postGoLiveFWStartDate: string;
  postGoLiveFWEndDate: string;
}

@Component({
  selector: 'app-schedule',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './schedule.component.html',
  styleUrl: './schedule.component.css',
})
export class ScheduleComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private _activePicker: HTMLInputElement | null = null;
  private _pickerResizeHandler: (() => void) | null = null;
  private dateFormatSvc = inject(DateFormatService);
  @Input() reviewId = '';
  @Input() singleMultipleLocation = '';
  @Input() isParentLocation = false;
  @Input() reviewStatus = '';
  @Input() basicDetailsDirty = false;
  @Input() savedFieldworkStartDate = '';
  @Input() savedFieldworkEndDate = '';
  @Input() savedServiceLine = '';
  @Input() savedParentChildType = '';
  @Input() savedParentReviewId = '';
  @Input() savedTargetPublicationDate = '';
  @Input() locationCards: ReviewLocationCard[] = [];
  @Input() isManagerRole = false;

  // ── Go-Live specific inputs ──────────────────────────────────────────────
  @Input() isGoLive = false;
  /** "Pre and Post Go-Live" | "Post Go-Live" — drives Pre section visibility. */
  @Input() typeOfReview = '';
  /** Go-Live date from Add Review data (or saved schedule after first submit). */
  @Input() goLiveDate = '';
  @Input() preGoLiveFWStartDate = '';
  @Input() preGoLiveFWEndDate = '';
  @Input() postGoLiveFWStartDate = '';
  @Input() postGoLiveFWEndDate = '';
  @Input() postGoLiveFWEndManuallyEdited = false;

  @Output() submitted = new EventEmitter<ScheduleForm>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() timelineGenerated = new EventEmitter<{ ScheduleData: any[]; TimeLine_Calculator: any[] }>();
  @Output() scheduled = new EventEmitter<{ status: string; data: any }>();
  @Output() missingTeamMembers = new EventEmitter<Array<{ cardIndex: number; role: string }>>();
  @Output() scrollToLocations = new EventEmitter<void>();

  form: ScheduleForm = {
    parentChild: 'Child',
    parentReviewId: '',
    serviceLines: [],
    fieldworkStartDate: '',
    fieldworkEndDate: '',
    goLiveDate: '',
    preGoLiveFWStartDate: '',
    preGoLiveFWEndDate: '',
    postGoLiveFWStartDate: '',
    postGoLiveFWEndDate: '',
  };

  parentChildOptions = ['Parent', 'Child'];
  parentReviewIdOptions: string[] = [];
  serviceLineOptions: string[] = [];

  serviceLineDropdownOpen = false;
  parentChildDropdownOpen = false;
  parentReviewIdDropdownOpen = false;
  parentReviewIdSearchTerm = '';
  isLoading = false;
  submitAttempted = false;
  showSubmitRoleError = false;

  // ── Standard review display texts (dd-MMM-yyyy) ─────────────────────────
  fieldworkStartDateText = '';
  fieldworkEndDateText = '';

  // ── Go-Live display texts ────────────────────────────────────────────────
  goLiveDateText = '';
  preGoLiveFWStartDateText = '';
  preGoLiveFWEndDateText = '';
  postGoLiveFWStartDateText = '';
  postGoLiveFWEndDateText = '';

  // ── Go-Live Date Changed popup ────────────────────────────────────────────
  showGoLiveDateChangedPopup = false;
  goLiveDateChangedAffectedDates: { label: string; value: string }[] = [];
  private _pendingGoLiveDate = '';
  private _previousGoLiveDateText = '';

  // ── Initial Go-Live values (for Cancel reset) ────────────────────────────
  private _initGoLiveDate = '';
  private _initPreGoLiveFWStartDate = '';
  private _initPreGoLiveFWEndDate = '';
  private _initPostGoLiveFWStartDate = '';
  private _initPostGoLiveFWEndDate = '';

  // ── STD/ODR/SPR Fieldwork Date Changed popup ────────────────────────────────
  showFWDateChangedPopup = false;
  fwDateChangedFieldLabel = '';
  private _scheduleEntryExists: number | null = null;
  private _pendingFWValue = '';
  private _pendingFWField: 'start' | 'end' = 'start';
  private _previousFWDate = '';
  private _previousFWDateText = '';

  // ── Per-field manual-edit tracking (in-session edits only) ──
  private _manuallyEditedFields = new Set<string>();
  // True if postGoLiveFWEndDate was edited in this session; never cleared by Recalculate.
  private _postGoLiveFWEndEditedThisSession = false;

  // ── Native date picker mounts ────────────────────────────────────────────
  startPickerMounted = true;
  endPickerMounted = true;
  glDatePickerMounted = true;
  preStartPickerMounted = true;
  preEndPickerMounted = true;
  postStartPickerMounted = true;
  postEndPickerMounted = true;

  @ViewChild('startPicker') startPickerRef?: ElementRef<HTMLInputElement>;
  @ViewChild('endPicker') endPickerRef?: ElementRef<HTMLInputElement>;
  @ViewChild('glDatePicker') glDatePickerRef?: ElementRef<HTMLInputElement>;
  @ViewChild('preStartPicker') preStartPickerRef?: ElementRef<HTMLInputElement>;
  @ViewChild('preEndPicker') preEndPickerRef?: ElementRef<HTMLInputElement>;
  @ViewChild('postStartPicker') postStartPickerRef?: ElementRef<HTMLInputElement>;
  @ViewChild('postEndPicker') postEndPickerRef?: ElementRef<HTMLInputElement>;

  constructor(private reviewerService: ReviewerService) {}

  ngOnInit(): void {
    this._pickerResizeHandler = () => {
      if (this._activePicker && !this._activePicker.disabled) {
        const picker = this._activePicker as HTMLInputElement & { showPicker?: () => void };
        picker.blur();
        setTimeout(() => picker.showPicker?.(), 0);
      }
    };
    window.addEventListener('resize', this._pickerResizeHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', this._pickerResizeHandler!));

    if (this.isReadonly) {
      this.form.parentChild = this.savedParentChildType || (this.isParentLocation ? 'Parent' : 'Child');
      this.form.parentReviewId = this.toReviewIdOption(this.savedParentReviewId);
      this.form.serviceLines = this.parseServiceLines(this.savedServiceLine);
      this.form.fieldworkStartDate = this.toInputDate(this.savedFieldworkStartDate);
      this.form.fieldworkEndDate = this.toInputDate(this.savedFieldworkEndDate);
    } else {
      this.form.parentChild = this.savedParentChildType || (this.isParentLocation ? 'Parent' : 'Child');
      this.form.parentReviewId = this.toReviewIdOption(this.savedParentReviewId);
      this.form.serviceLines = this.parseServiceLines(this.savedServiceLine);
      this.form.fieldworkStartDate = this.toInputDate(this.savedFieldworkStartDate);
      this.form.fieldworkEndDate = this.toInputDate(this.savedFieldworkEndDate);
    }

    // Saved parent ID equal to own ID means this review is the group's parent, whatever the stale type says.
    if (this.isSavedSelfParent) {
      this.form.parentChild = 'Parent';
      this.form.parentReviewId = '';
    }

    // Go-Live: always pre-populate from inputs regardless of editable vs readonly state.
    // On first open (Planned): inputs hold Add Review values.
    // After schedule saved: inputs hold saved schedule values (updated via onScheduled in parent).
    if (this.isGoLive) {
      this.form.goLiveDate = this.toInputDate(this.goLiveDate);
      this.form.postGoLiveFWStartDate = this.toInputDate(this.postGoLiveFWStartDate);
      this.form.postGoLiveFWEndDate = this.toInputDate(this.postGoLiveFWEndDate);
      if (this.isPreAndPost) {
        this.form.preGoLiveFWStartDate = this.toInputDate(this.preGoLiveFWStartDate);
        this.form.preGoLiveFWEndDate = this.toInputDate(this.preGoLiveFWEndDate);
      }
      this._initGoLiveDate = this.form.goLiveDate;
      this._initPreGoLiveFWStartDate = this.form.preGoLiveFWStartDate;
      this._initPreGoLiveFWEndDate = this.form.preGoLiveFWEndDate;
      this._initPostGoLiveFWStartDate = this.form.postGoLiveFWStartDate;
      this._initPostGoLiveFWEndDate = this.form.postGoLiveFWEndDate;
      this.goLiveDateText = this.formatDisplayDate(this.form.goLiveDate);
      this.preGoLiveFWStartDateText = this.formatDisplayDate(this.form.preGoLiveFWStartDate);
      this.preGoLiveFWEndDateText = this.formatDisplayDate(this.form.preGoLiveFWEndDate);
      this.postGoLiveFWStartDateText = this.formatDisplayDate(this.form.postGoLiveFWStartDate);
      this.postGoLiveFWEndDateText = this.formatDisplayDate(this.form.postGoLiveFWEndDate);
    }

    if (!this.isGoLive) {
      this.loadScheduleEntryExists();
    }

    this.fieldworkStartDateText = this.formatDisplayDate(this.form.fieldworkStartDate);
    this.fieldworkEndDateText = this.formatDisplayDate(this.form.fieldworkEndDate);
    this.loadServiceLines();
    this.loadParentReviewIds();

    if (this.reviewId) {
      this.reviewerService.getScheduleReviewDetailsDynamic(Number(this.reviewId))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result: any) => {
            const d = Array.isArray(result) ? result[0] : result;
            if (d?.S_ServiceLine) {
              this.form.serviceLines = this.parseServiceLines(d.S_ServiceLine);
            }
          }
        });
    }
  }

  // ── Go-Live computed properties ──────────────────────────────────────────

  /** True when typeOfReview includes "Pre" (i.e. "Pre and Post Go-Live"). */
  get isPreAndPost(): boolean {
    return this.typeOfReview.toLowerCase().includes('pre');
  }

  /**
   * GLRR Interim Call Date is Go-Live Date minus 20 working days.
   * Shown as a read-only derived field in the Pre & Post layout.
   */
  get glrrInterimCallDate(): string {
    const goLive = this.parseLocalDate(this.form.goLiveDate);
    if (!goLive) return '';
    return this.formatDisplayDate(this.formatIsoDate(this.addWorkingDays(goLive, GLRR_FIELD_CONFIG.INTERIM_CALL_FROM_GO_LIVE)));
  }

  /**
   * Publish Final Report: formula-calculated from Post Go-Live FW End Date.
   * Same working-day calculation as Target Report Publication Date.
   */
  get publishFinalReport(): string {
    if (this.datesReadonly && this.savedTargetPublicationDate) {
      return this.formatDisplayDate(this.savedTargetPublicationDate);
    }
    const target = this.computeTargetPublicationDate(this.form.postGoLiveFWEndDate);
    if (!target) return '';
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const dd = String(target.getDate()).padStart(2, '0');
    return `${dd}-${MONTHS[target.getMonth()]}-${target.getFullYear()}`;
  }

  // ── Staffing validation ──────────────────────────────────────────────────

  /** True when at least one location card is missing a primary Manager, Supervisor, or Reviewer. */
  get hasMissingPrimaryRoles(): boolean {
    return this.locationCards.some(
      card => !card.rcManager || !card.rcSupervisor || !card.rcReviewer
    );
  }

  // ── Readonly / locked logic ──────────────────────────────────────────────

  /**
   * For standard reviews: dates lock once status moves beyond Planned.
   * For Go-Live reviews: dates remain editable until POST-Go-Live FW-Started.
   * For STD/ODR/SPR reviews: dates are always editable regardless of status.
   * All date fields lock once status is FW Started, Pre Go-Live FW Started, or Post Go-Live FW Started.
   */
  get datesReadonly(): boolean {
    if (this.isFwStarted) return true;
    if (this.isGoLive) {
      const s = (this.reviewStatus || '').toLowerCase().replace(/[-]+/g, ' ').replace(/\s+/g, ' ').trim();
      return s.includes('post go live fw started') || s.includes('post go live fieldwork started');
    }
    return false;
  }

  get isReadonly(): boolean {
    return this.datesReadonly;
  }

  // ── Date validation ──────────────────────────────────────────────────────

  // ── Standard review error getters ────────────────────────────────────────

  get startDateError(): string {
    if (this.datesReadonly) return '';
    if (!this.form.fieldworkStartDate) {
      return this.submitAttempted ? 'Fieldwork Start Date is Mandatory.' : '';
    }
    return '';
  }

  get endDateError(): string {
    if (this.datesReadonly) return '';
    if (!this.form.fieldworkEndDate) {
      return this.submitAttempted ? 'Fieldwork End Date is Mandatory.' : '';
    }
    if (this.form.fieldworkStartDate && this.form.fieldworkEndDate < this.form.fieldworkStartDate) {
      return 'Fieldwork End Date cannot be earlier than the Start Date.';
    }
    return '';
  }

  get datesValid(): boolean {
    return (
      !!this.form.fieldworkStartDate &&
      !!this.form.fieldworkEndDate &&
      this.form.fieldworkEndDate >= this.form.fieldworkStartDate
    );
  }

  // ── Go-Live error getters ─────────────────────────────────────────────────

  // Generated by GitHub Copilot - Oct-07-2026
  /** Shows saved self-parent errors immediately and required errors after submission. */
  get parentReviewIdError(): string {
    if (this.form.parentChild === 'Child' && this.isOwnReviewId(this.form.parentReviewId)) {
      return 'A review cannot be its own parent.';
    }
    if (!this.isGoLive || this.isReadonly) return '';
    if (this.form.parentChild === 'Child' && !this.form.parentReviewId) {
      return this.submitAttempted ? 'Parent Review ID is required when review is set as Child.' : '';
    }
    return '';
  }

  get goLiveDateError(): string {
    if (this.datesReadonly) return '';
    if (!this.form.goLiveDate) {
      return this.submitAttempted ? 'Go-Live Date is required.' : '';
    }
    return '';
  }

  get preGoLiveFWStartDateError(): string {
    if (this.datesReadonly || !this.isPreAndPost) return '';
    if (!this.form.preGoLiveFWStartDate) {
      return this.submitAttempted ? 'Pre Go-Live Fieldwork Start Date is required.' : '';
    }
    return '';
  }

  get preGoLiveFWEndDateError(): string {
    if (this.datesReadonly || !this.isPreAndPost) return '';
    if (!this.form.preGoLiveFWEndDate) {
      return this.submitAttempted ? 'Pre Go-Live Fieldwork End Date is required.' : '';
    }
    if (this.form.preGoLiveFWStartDate && this.form.preGoLiveFWEndDate < this.form.preGoLiveFWStartDate) {
      return 'Pre Go-Live Fieldwork End Date cannot be earlier than Pre Go-Live Fieldwork Start Date.';
    }
    return '';
  }

  get postGoLiveFWStartDateError(): string {
    if (this.datesReadonly) return '';
    if (!this.form.postGoLiveFWStartDate) {
      return this.submitAttempted ? 'Post Go-Live Fieldwork Start Date is required.' : '';
    }
    return '';
  }

  get postGoLiveFWEndDateError(): string {
    if (this.datesReadonly) return '';
    if (!this.form.postGoLiveFWEndDate) {
      return this.submitAttempted ? 'Post Go-Live Fieldwork End Date is required.' : '';
    }
    if (this.form.postGoLiveFWStartDate && this.form.postGoLiveFWEndDate < this.form.postGoLiveFWStartDate) {
      return 'Post Go-Live Fieldwork End Date cannot be earlier than Post Go-Live Fieldwork Start Date.';
    }
    return '';
  }

  /** All required Go-Live date fields present and sequences valid. */
  get goLiveDatesValid(): boolean {
    // Generated by GitHub Copilot - Oct-07-2026
    if (this.form.parentChild === 'Child' &&
        (!this.form.parentReviewId || this.isOwnReviewId(this.form.parentReviewId))) return false;
    if (!this.form.goLiveDate) return false;
    if (!this.form.postGoLiveFWStartDate) return false;
    if (!this.form.postGoLiveFWEndDate) return false;
    if (this.form.postGoLiveFWEndDate < this.form.postGoLiveFWStartDate) return false;
    if (this.isPreAndPost) {
      if (!this.form.preGoLiveFWStartDate) return false;
      if (!this.form.preGoLiveFWEndDate) return false;
      if (this.form.preGoLiveFWEndDate < this.form.preGoLiveFWStartDate) return false;
    }
    return true;
  }

  // ── Date formatting / parsing ────────────────────────────────────────────

  private toInputDate(value: string): string {
    if (!value) return '';
    const v = value.trim();
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const dmmmy = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(v);
    if (dmmmy) {
      const MONTH_MAP: Record<string, string> = {
        jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',
        jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12'
      };
      const mm = MONTH_MAP[dmmmy[2].toLowerCase()];
      if (mm) return `${dmmmy[3]}-${mm}-${dmmmy[1].padStart(2, '0')}`;
    }
    const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(v);
    if (dmy) {
      return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
    }
    return '';
  }

  private formatDisplayDate(value: string): string {
    return this.dateFormatSvc.format(value);
  }

  private parseDisplayDate(text: string): string {
    const iso = this.toInputDate(text);
    if (!iso) return '';
    const d = this.parseLocalDate(iso);
    return d && this.formatIsoDate(d) === iso ? iso : '';
  }

  get fieldworkStartDateDisplay(): string { return this.formatDisplayDate(this.form.fieldworkStartDate); }
  get fieldworkEndDateDisplay(): string { return this.formatDisplayDate(this.form.fieldworkEndDate); }

  get glLiveDateDisplay(): string { return this.formatDisplayDate(this.form.goLiveDate); }
  get preGoLiveFWStartDisplay(): string { return this.formatDisplayDate(this.form.preGoLiveFWStartDate); }
  get preGoLiveFWEndDisplay(): string { return this.formatDisplayDate(this.form.preGoLiveFWEndDate); }
  get postGoLiveFWStartDisplay(): string { return this.formatDisplayDate(this.form.postGoLiveFWStartDate); }
  get postGoLiveFWEndDisplay(): string { return this.formatDisplayDate(this.form.postGoLiveFWEndDate); }

  // ── Schedule ID / Review ID helpers ─────────────────────────────────────

  get scheduleId(): string { return this.reviewId ? `S-${this.reviewId}` : ''; }

  private get reviewIdNumber(): number | null { return this.toReviewIdNumber(this.reviewId); }

  private toReviewIdNumber(value: string): number | null {
    if (!value) return null;
    const num = Number(String(value).replace(/^R-/i, ''));
    return isNaN(num) ? null : num;
  }

  private toReviewIdOption(value: string): string {
    if (!value) return '';
    return /^R-/i.test(value) ? value : `R-${value}`;
  }

  private parseServiceLines(value: string): string[] {
    if (!value) return [];
    return value.split(',').map(s => s.trim()).filter(s => !!s);
  }

  // ── Parent/Child visibility ──────────────────────────────────────────────

  get showParentReviewId(): boolean { return this.form.parentChild === 'Child'; }

  // Generated by GitHub Copilot - Oct-07-2026
  /** Compares numeric review IDs regardless of their display prefix. */
  private isOwnReviewId(value: string): boolean {
    const currentId = this.reviewIdNumber;
    return currentId !== null && this.toReviewIdNumber(value) === currentId;
  }

  private get isSavedSelfParent(): boolean {
    return !!this.savedParentReviewId && this.isOwnReviewId(this.savedParentReviewId);
  }

  get parentChildReadonly(): boolean {
    const isMulti = (this.singleMultipleLocation || '').toLowerCase().startsWith('multi');
    return !isMulti && this.isParentLocation && this.parentReviewIdOptions.length === 1;
  }

  // Generated by GitHub Copilot - Oct-07-2026
  /** Excludes the current review for every Child review. */
  get displayedParentReviewIdOptions(): string[] {
    if (this.form.parentChild === 'Child') {
      return this.parentReviewIdOptions.filter(option => !this.isOwnReviewId(option));
    }
    return this.parentReviewIdOptions;
  }

  get filteredParentReviewIdOptions(): string[] {
    const term = this.parentReviewIdSearchTerm.trim().toLowerCase();
    if (!term) return this.displayedParentReviewIdOptions;
    return this.displayedParentReviewIdOptions.filter(o => o.toLowerCase().includes(term));
  }

  // ── Target Report Publication Date (standard reviews) ───────────────────

  get targetReportPublicationDate(): string {
    if (this.datesReadonly && this.savedTargetPublicationDate) {
      return this.formatDisplayDate(this.savedTargetPublicationDate);
    }
    if (!this.datesReadonly && !this.datesValid) return '';
    const target = this.computeTargetPublicationDate();
    if (!target) return '';
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const dd = String(target.getDate()).padStart(2, '0');
    return `${dd}-${MONTHS[target.getMonth()]}-${target.getFullYear()}`;
  }

  // ── Date arithmetic ──────────────────────────────────────────────────────

  /**
   * Compute the Target Report Publication Date from a fieldwork end date.
   * When called without argument, uses the standard fieldwork end date (standard reviews).
   * Pass postGoLiveFWEndDate for Go-Live Publish Final Report.
   */
  private computeTargetPublicationDate(endDateOverride?: string): Date | null {
    const end = this.parseLocalDate(endDateOverride ?? this.form.fieldworkEndDate);
    if (!end) return null;
    const isMulti = (this.singleMultipleLocation || '').toLowerCase().startsWith('multi');
    const workingDaysToAdd = isMulti ? TARGET_PUB_WORKING_DAYS_MULTI : TARGET_PUB_WORKING_DAYS_SINGLE;
    const target = this.addWorkingDays(end, workingDaysToAdd);
    const weekday = target.getDay();
    if (weekday === 6) target.setDate(target.getDate() + 2);
    else if (weekday === 0) target.setDate(target.getDate() + 1);
    return target;
  }

  private addWorkingDays(start: Date, days: number): Date {
    const result = new Date(start.getTime());
    const step = days < 0 ? -1 : 1;
    let remaining = Math.abs(days);
    while (remaining > 0) {
      result.setDate(result.getDate() + step);
      const day = result.getDay();
      if (day !== 0 && day !== 6) remaining--;
    }
    return result;
  }

  private parseLocalDate(value: string): Date | null {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    const date = new Date(y, m - 1, d);
    return isNaN(date.getTime()) ? null : date;
  }

  private formatIsoDate(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${mm}-${dd}`;
  }

  // ── Schedule entry check (STD/ODR/SPR) ──────────────────────────────────

  private loadScheduleEntryExists(): void {
    const id = this.reviewIdNumber;
    if (id === null) return;
    this.reviewerService.checkScheduleEntryExists(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const items = Array.isArray(response) ? response : (response ? [response] : []);
          const item = items[0] ?? {};
          const raw = item?.IfExists ?? item?.ifExists;
          this._scheduleEntryExists = raw !== undefined ? Number(raw) : null;
        },
        error: () => { this._scheduleEntryExists = null; },
      });
  }

  // ── Service line API ─────────────────────────────────────────────────────

  private loadServiceLines(): void {
    this.isLoading = true;
    this.reviewerService.getServiceLines()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const items = Array.isArray(response) ? response : (response?.value ?? []);
          this.serviceLineOptions = items
            .map((item: any) => this.extractServiceLineName(item))
            .filter((name: string) => !!name && name.trim().toLowerCase() !== 'other');
          this.isLoading = false;
        },
        error: (err) => { console.error('GetServiceLines error:', err?.message); this.isLoading = false; },
      });
  }

  private extractServiceLineName(item: any): string {
    if (typeof item === 'string') return item;
    return item?.ServiceLineName ?? item?.ServiceLine ?? item?.Name ?? item?.Value ?? '';
  }

  private loadParentReviewIds(): void {
    const id = Number(this.reviewId);
    if (!this.reviewId || isNaN(id)) return;
    this.reviewerService.getReviewGroupByID(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const items = Array.isArray(response) ? response : (response?.value ?? response?.recordset ?? []);
          this.parentReviewIdOptions = items
            .map((item: any) => this.extractParentReviewId(item))
            .filter((value: string) => !!value);
        },
        error: (err) => console.error('GetReviewGroupByID error:', err?.message),
      });
  }

  private extractParentReviewId(item: any): string {
    if (typeof item === 'string') return item;
    const id = item?.ReviewID ?? item?.ParentReviewID ?? item?.ReviewId ?? item?.ParentReviewId;
    if (id !== undefined && id !== null && id !== '') {
      const value = String(id);
      return /^R-/i.test(value) ? value : `R-${value}`;
    }
    return item?.ReviewName ?? item?.ReviewGroupName ?? '';
  }

  // ── Dirty tracking ────────────────────────────────────────────────────────


  // ── Dropdown handlers ────────────────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.serviceLineDropdownOpen && !target.closest('.service-line-select')) {
      this.serviceLineDropdownOpen = false;
    }
    if (this.parentChildDropdownOpen && !target.closest('.parent-child-select')) {
      this.parentChildDropdownOpen = false;
    }
    if (this.parentReviewIdDropdownOpen && !target.closest('.parent-review-id-select')) {
      this.parentReviewIdDropdownOpen = false;
      this.parentReviewIdSearchTerm = '';
    }
  }

  toggleServiceLineDropdown(): void {
    if (this.isReadonly) return;
    this.serviceLineDropdownOpen = !this.serviceLineDropdownOpen;
    if (this.serviceLineDropdownOpen) {
      this.parentChildDropdownOpen = false;
      this.parentReviewIdDropdownOpen = false;
    }
  }

  toggleParentChildDropdown(): void {
    if (this.parentChildReadonly || this.isReadonly) return;
    this.parentChildDropdownOpen = !this.parentChildDropdownOpen;
    if (this.parentChildDropdownOpen) {
      this.serviceLineDropdownOpen = false;
      this.parentReviewIdDropdownOpen = false;
    }
  }

  selectParentChild(option: string): void {
    this.form.parentChild = option;
    this.parentChildDropdownOpen = false;
    if (option === 'Parent' || !this.displayedParentReviewIdOptions.includes(this.form.parentReviewId)) {
      this.form.parentReviewId = '';
    }
  }

  toggleParentReviewIdDropdown(): void {
    if (this.isReadonly) return;
    this.parentReviewIdDropdownOpen = !this.parentReviewIdDropdownOpen;
    if (this.parentReviewIdDropdownOpen) {
      this.serviceLineDropdownOpen = false;
      this.parentChildDropdownOpen = false;
      this.parentReviewIdSearchTerm = '';
    }
  }

  // Generated by GitHub Copilot - Oct-07-2026
  /** Refuses self-parent selections even if called outside the filtered dropdown. */
  selectParentReviewId(option: string): void {
    if (this.form.parentChild === 'Child' && this.isOwnReviewId(option)) return;
    this.form.parentReviewId = option;
    this.parentReviewIdDropdownOpen = false;
    this.parentReviewIdSearchTerm = '';
  }

  isServiceLineSelected(option: string): boolean { return this.form.serviceLines.includes(option); }

  toggleServiceLine(option: string): void {
    if (this.isServiceLineSelected(option)) {
      this.form.serviceLines = this.form.serviceLines.filter(s => s !== option);
    } else {
      this.form.serviceLines = [...this.form.serviceLines, option];
    }
    this.serviceLineDropdownOpen = false;
  }

  removeServiceLine(option: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.form.serviceLines = this.form.serviceLines.filter(s => s !== option);
  }

  // ── Standard date text change / calendar handlers ────────────────────────

  onStartDateTextChange(text: string): void {
    this.fieldworkStartDateText = text;
    const parsed = this.parseDisplayDate(text);
    if (!this.isGoLive && this._scheduleEntryExists === 1 && parsed && parsed !== this.form.fieldworkStartDate && this.form.fieldworkStartDate) {
      this._previousFWDate = this.form.fieldworkStartDate;
      this._previousFWDateText = this.formatDisplayDate(this.form.fieldworkStartDate);
      this._pendingFWValue = parsed;
      this._pendingFWField = 'start';
      this.fwDateChangedFieldLabel = 'Start';
      this.openFWDatePopup();
      return;
    }
    this.form.fieldworkStartDate = parsed;
  }

  onEndDateTextChange(text: string): void {
    this.fieldworkEndDateText = text;
    const parsed = this.parseDisplayDate(text);
    if (!this.isGoLive && this._scheduleEntryExists === 1 && parsed && parsed !== this.form.fieldworkEndDate && this.form.fieldworkEndDate) {
      this._previousFWDate = this.form.fieldworkEndDate;
      this._previousFWDateText = this.formatDisplayDate(this.form.fieldworkEndDate);
      this._pendingFWValue = parsed;
      this._pendingFWField = 'end';
      this.fwDateChangedFieldLabel = 'End';
      this.openFWDatePopup();
      return;
    }
    this.form.fieldworkEndDate = parsed;
  }

  onStartDatePicked(isoValue: string): void {
    if (!this.isGoLive && this._scheduleEntryExists === 1 && isoValue !== this.form.fieldworkStartDate && this.form.fieldworkStartDate) {
      this._previousFWDate = this.form.fieldworkStartDate;
      this._previousFWDateText = this.formatDisplayDate(this.form.fieldworkStartDate);
      this._pendingFWValue = isoValue;
      this._pendingFWField = 'start';
      this.fwDateChangedFieldLabel = 'Start';
      this.fieldworkStartDateText = this.formatDisplayDate(isoValue);
      this.openFWDatePopup();
    } else {
      this.form.fieldworkStartDate = isoValue;
      this.fieldworkStartDateText = this.formatDisplayDate(isoValue);
    }
    this.remountPicker(() => (this.startPickerMounted = true));
    this.startPickerMounted = false;
  }

  onEndDatePicked(isoValue: string): void {
    if (!this.isGoLive && this._scheduleEntryExists === 1 && isoValue !== this.form.fieldworkEndDate && this.form.fieldworkEndDate) {
      this._previousFWDate = this.form.fieldworkEndDate;
      this._previousFWDateText = this.formatDisplayDate(this.form.fieldworkEndDate);
      this._pendingFWValue = isoValue;
      this._pendingFWField = 'end';
      this.fwDateChangedFieldLabel = 'End';
      this.fieldworkEndDateText = this.formatDisplayDate(isoValue);
      this.openFWDatePopup();
    } else {
      this.form.fieldworkEndDate = isoValue;
      this.fieldworkEndDateText = this.formatDisplayDate(isoValue);
    }
    this.remountPicker(() => (this.endPickerMounted = true));
    this.endPickerMounted = false;
  }

  // ── Go-Live date text change / calendar handlers ─────────────────────────

  onGoLiveDateTextChange(text: string): void {
    this.goLiveDateText = text;
    const parsed = this.parseDisplayDate(text);

    // No valid date, same date, or no existing date to protect — just update form
    if (!parsed || parsed === this.form.goLiveDate || !this.form.goLiveDate) {
      this.form.goLiveDate = parsed;
      return;
    }

    this.buildGoLiveDateChangedDates();
    if (this.goLiveDateChangedAffectedDates.length === 0) {
      // No manually-edited dates in this session — silently recalculate
      this.form.goLiveDate = parsed;
      this.recalculateAutoFieldsSilently(parsed);
      return;
    }
    this._previousGoLiveDateText = this.formatDisplayDate(this.form.goLiveDate);
    this._pendingGoLiveDate = parsed;
    this.openGoLiveDatePopup();
  }

  onGoLiveDatePicked(isoValue: string): void {
    if (isoValue !== this.form.goLiveDate && this.form.goLiveDate) {
      this.buildGoLiveDateChangedDates();
      if (this.goLiveDateChangedAffectedDates.length === 0) {
        // No manually-edited dates in this session — silently recalculate
        this.form.goLiveDate = isoValue;
        this.goLiveDateText = this.formatDisplayDate(isoValue);
        this.recalculateAutoFieldsSilently(isoValue);
      } else {
        this._previousGoLiveDateText = this.formatDisplayDate(this.form.goLiveDate);
        this._pendingGoLiveDate = isoValue;
        this.goLiveDateText = this.formatDisplayDate(isoValue);
        this.openGoLiveDatePopup();
      }
    } else {
      this.form.goLiveDate = isoValue;
      this.goLiveDateText = this.formatDisplayDate(isoValue);
    }
    this.remountPicker(() => (this.glDatePickerMounted = true));
    this.glDatePickerMounted = false;
  }

  private buildGoLiveDateChangedDates(): void {
    const dates: { label: string; value: string }[] = [];

    if (this.isPreAndPost) {
      if (this._manuallyEditedFields.has('preGoLiveFWStartDate') && this.form.preGoLiveFWStartDate) {
        dates.push({ label: 'Pre Go-Live Fieldwork Start Date', value: this.formatDisplayDate(this.form.preGoLiveFWStartDate) });
      }
      if (this._manuallyEditedFields.has('preGoLiveFWEndDate') && this.form.preGoLiveFWEndDate) {
        dates.push({ label: 'Pre Go-Live Fieldwork End Date', value: this.formatDisplayDate(this.form.preGoLiveFWEndDate) });
      }
    }
    if (this._manuallyEditedFields.has('postGoLiveFWStartDate') && this.form.postGoLiveFWStartDate) {
      dates.push({ label: 'Post Go-Live Fieldwork Start Date', value: this.formatDisplayDate(this.form.postGoLiveFWStartDate) });
    }
    if (this._postGoLiveFWEndEditedThisSession && this.form.postGoLiveFWEndDate) {
      dates.push({ label: 'Post Go-Live Fieldwork End Date', value: this.formatDisplayDate(this.form.postGoLiveFWEndDate) });
    }
    this.goLiveDateChangedAffectedDates = dates;
  }

  onCancelGoLiveDateChange(): void {
    this.closeGoLiveDatePopup();
    this.goLiveDateText = this._previousGoLiveDateText;
    this._pendingGoLiveDate = '';
  }

  onKeepMyDates(): void {
    this.closeGoLiveDatePopup();
    this.goLiveDateText = this._previousGoLiveDateText;
    this._pendingGoLiveDate = '';
  }

  onRecalculate(): void {
    const newGoLiveDate = this._pendingGoLiveDate;
    this.form.goLiveDate = newGoLiveDate;
    this.recalculateAutoFieldsSilently(newGoLiveDate);
    // All applicable auto fields are now recalculated — clear manual-edit tracking
    this._manuallyEditedFields.clear();
    this.closeGoLiveDatePopup();
    this._pendingGoLiveDate = '';
  }

  /** Recalculates preGoLiveFWStart, preGoLiveFWEnd (Pre+Post only), and postGoLiveFWStart.
   *  postGoLiveFWEndDate is manually entered and is never cleared or recalculated. */
  private recalculateAutoFieldsSilently(newGoLiveDate: string): void {
    const goLive = this.parseLocalDate(newGoLiveDate);
    if (!goLive) return;
    const postStart = this.addWorkingDays(goLive, GLRR_FIELD_CONFIG.POST_FW_START_FROM_GO_LIVE);
    this.form.postGoLiveFWStartDate = this.formatIsoDate(postStart);
    this.postGoLiveFWStartDateText = this.formatDisplayDate(this.form.postGoLiveFWStartDate);
    if (this.isPreAndPost) {
      const preStart = this.addWorkingDays(goLive, GLRR_FIELD_CONFIG.PRE_FW_START_FROM_GO_LIVE);
      const preEnd = this.addWorkingDays(goLive, GLRR_FIELD_CONFIG.PRE_FW_END_FROM_GO_LIVE);
      this.form.preGoLiveFWStartDate = this.formatIsoDate(preStart);
      this.form.preGoLiveFWEndDate = this.formatIsoDate(preEnd);
      this.preGoLiveFWStartDateText = this.formatDisplayDate(this.form.preGoLiveFWStartDate);
      this.preGoLiveFWEndDateText = this.formatDisplayDate(this.form.preGoLiveFWEndDate);
    }
  }

  private openGoLiveDatePopup(): void {
    this.showGoLiveDateChangedPopup = true;
    document.body.style.overflow = 'hidden';
  }

  private closeGoLiveDatePopup(): void {
    this.showGoLiveDateChangedPopup = false;
    document.body.style.overflow = '';
  }

  onPreGoLiveFWStartTextChange(text: string): void {
    this.preGoLiveFWStartDateText = text;
    const parsed = this.parseDisplayDate(text);
    if (parsed) this._manuallyEditedFields.add('preGoLiveFWStartDate');
    this.form.preGoLiveFWStartDate = parsed;
  }

  onPreGoLiveFWStartPicked(isoValue: string): void {
    this.form.preGoLiveFWStartDate = isoValue;
    this._manuallyEditedFields.add('preGoLiveFWStartDate');
    this.preGoLiveFWStartDateText = this.formatDisplayDate(isoValue);
    this.remountPicker(() => (this.preStartPickerMounted = true));
    this.preStartPickerMounted = false;
  }

  onPreGoLiveFWEndTextChange(text: string): void {
    this.preGoLiveFWEndDateText = text;
    const parsed = this.parseDisplayDate(text);
    if (parsed) this._manuallyEditedFields.add('preGoLiveFWEndDate');
    this.form.preGoLiveFWEndDate = parsed;
  }

  onPreGoLiveFWEndPicked(isoValue: string): void {
    this.form.preGoLiveFWEndDate = isoValue;
    this._manuallyEditedFields.add('preGoLiveFWEndDate');
    this.preGoLiveFWEndDateText = this.formatDisplayDate(isoValue);
    this.remountPicker(() => (this.preEndPickerMounted = true));
    this.preEndPickerMounted = false;
  }

  onPostGoLiveFWStartTextChange(text: string): void {
    this.postGoLiveFWStartDateText = text;
    const parsed = this.parseDisplayDate(text);
    if (parsed) this._manuallyEditedFields.add('postGoLiveFWStartDate');
    this.form.postGoLiveFWStartDate = parsed;
  }

  onPostGoLiveFWStartPicked(isoValue: string): void {
    this.form.postGoLiveFWStartDate = isoValue;
    this._manuallyEditedFields.add('postGoLiveFWStartDate');
    this.postGoLiveFWStartDateText = this.formatDisplayDate(isoValue);
    this.remountPicker(() => (this.postStartPickerMounted = true));
    this.postStartPickerMounted = false;
  }

  onPostGoLiveFWEndTextChange(text: string): void {
    this.postGoLiveFWEndDateText = text;
    const parsed = this.parseDisplayDate(text);
    if (parsed) { this._manuallyEditedFields.add('postGoLiveFWEndDate'); this._postGoLiveFWEndEditedThisSession = true; }
    this.form.postGoLiveFWEndDate = parsed;
  }

  onPostGoLiveFWEndPicked(isoValue: string): void {
    this.form.postGoLiveFWEndDate = isoValue;
    this._manuallyEditedFields.add('postGoLiveFWEndDate');
    this._postGoLiveFWEndEditedThisSession = true;
    this.postGoLiveFWEndDateText = this.formatDisplayDate(isoValue);
    this.remountPicker(() => (this.postEndPickerMounted = true));
    this.postEndPickerMounted = false;
  }

  // ── Fieldwork Date Changed popup handlers (STD/ODR/SPR) ─────────────────

  onConfirmFWDateChange(): void {
    if (this._pendingFWField === 'start') {
      this.form.fieldworkStartDate = this._pendingFWValue;
      this.fieldworkStartDateText = this.formatDisplayDate(this._pendingFWValue);
    } else {
      this.form.fieldworkEndDate = this._pendingFWValue;
      this.fieldworkEndDateText = this.formatDisplayDate(this._pendingFWValue);
    }
    this._pendingFWValue = '';
    this.closeFWDatePopup();
  }

  onCancelFWDateChange(): void {
    if (this._pendingFWField === 'start') {
      this.fieldworkStartDateText = this._previousFWDateText;
    } else {
      this.fieldworkEndDateText = this._previousFWDateText;
    }
    this._pendingFWValue = '';
    this.closeFWDatePopup();
  }

  private openFWDatePopup(): void {
    this.showFWDateChangedPopup = true;
    document.body.style.overflow = 'hidden';
  }

  private closeFWDatePopup(): void {
    this.showFWDateChangedPopup = false;
    document.body.style.overflow = '';
  }

  // ── Calendar icon helper ─────────────────────────────────────────────────

  openDatePicker(el: HTMLInputElement | undefined): void {
    if (!el) return;
    const picker = el as HTMLInputElement & { showPicker?: () => void };
    if (typeof picker.showPicker === 'function') {
      this._activePicker = el;
      picker.showPicker();
    } else {
      el.focus();
      el.click();
    }
  }

  private remountPicker(remount: () => void): void {
    (document.activeElement as HTMLElement | null)?.blur?.();
    const raf = typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (cb: FrameRequestCallback) => setTimeout(() => cb(0), 16);
    raf(() => raf(() => remount()));
  }

  // ── Cancel / Submit ──────────────────────────────────────────────────────

  onCancel(): void {
    const restoredStartDate = this.toInputDate(this.savedFieldworkStartDate);
    const restoredEndDate = this.toInputDate(this.savedFieldworkEndDate);
    this.form = {
      parentChild: this.isSavedSelfParent || this.isParentLocation ? 'Parent' : 'Child',
      parentReviewId: this.isSavedSelfParent ? '' : this.toReviewIdOption(this.savedParentReviewId),
      serviceLines: [],
      fieldworkStartDate: restoredStartDate,
      fieldworkEndDate: restoredEndDate,
      goLiveDate: this._initGoLiveDate,
      preGoLiveFWStartDate: this._initPreGoLiveFWStartDate,
      preGoLiveFWEndDate: this._initPreGoLiveFWEndDate,
      postGoLiveFWStartDate: this._initPostGoLiveFWStartDate,
      postGoLiveFWEndDate: this._initPostGoLiveFWEndDate,
    };
    this.fieldworkStartDateText = this.formatDisplayDate(restoredStartDate);
    this.fieldworkEndDateText = this.formatDisplayDate(restoredEndDate);
    if (this.isGoLive) {
      this.goLiveDateText = this.formatDisplayDate(this._initGoLiveDate);
      this.preGoLiveFWStartDateText = this.formatDisplayDate(this._initPreGoLiveFWStartDate);
      this.preGoLiveFWEndDateText = this.formatDisplayDate(this._initPreGoLiveFWEndDate);
      this.postGoLiveFWStartDateText = this.formatDisplayDate(this._initPostGoLiveFWStartDate);
      this.postGoLiveFWEndDateText = this.formatDisplayDate(this._initPostGoLiveFWEndDate);
    }
    this.submitAttempted = false;
    this.showSubmitRoleError = false;
    this._manuallyEditedFields.clear();
    this._postGoLiveFWEndEditedThisSession = false;
    this.serviceLineDropdownOpen = false;
    this.parentChildDropdownOpen = false;
    this.parentReviewIdDropdownOpen = false;
    this.parentReviewIdSearchTerm = '';
    this.cancelled.emit();
  }

  onSubmit(): void {
    if (this.isReadonly) return;

    if (this.isManagerRole && this.locationCards.length > 0) {
      const missing: { cardIndex: number; role: string }[] = [];
      for (let i = 0; i < this.locationCards.length; i++) {
        const card = this.locationCards[i];
        if (!card.rcManager) missing.push({ cardIndex: i, role: 'manager' });
        if (!card.rcSupervisor) missing.push({ cardIndex: i, role: 'supervisor' });
        if (!card.rcReviewer) missing.push({ cardIndex: i, role: 'reviewer' });
      }
      if (missing.length > 0) { this.showSubmitRoleError = true; this.missingTeamMembers.emit(missing); return; }
    }

    // Block submission for all roles when primary staffing is incomplete
    if (this.locationCards.length > 0 && this.hasMissingPrimaryRoles) {
      this.showSubmitRoleError = true;
      this.scrollToLocations.emit();
      return;
    }

    this.submitAttempted = true;

    if (this.isGoLive) {
      if (!this.goLiveDatesValid) return;
    } else {
      if (!this.datesReadonly && !this.datesValid) return;
    }

    const submitPayload = this.buildSubmitPayload();
    this.timelineGenerated.emit(submitPayload);

    this.isLoading = true;
    this.reviewerService.createSchedule(this.buildSaveSchedulePayload())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.submitted.emit(this.form);
          const items = Array.isArray(response) ? response : (response ? [response] : []);
          const created = items.some((item: any) =>
            item?.Message === 'Review Milestone Created Successfully' ||
            item?.ReviewStatus === 'Scheduled'
          );
          if (created) {
            this.reviewStatus = 'Scheduled';
          }
          this.scheduled.emit({ status: created ? 'Scheduled' : this.reviewStatus, data: { ...submitPayload.ScheduleData[0], _postGoLiveFWEndManuallyEdited: this._postGoLiveFWEndEditedThisSession || this.postGoLiveFWEndManuallyEdited } });
          this.isLoading = false;
        },
        error: (err) => { console.error('CreateSchedule error:', err?.message); this.isLoading = false; },
      });
  }

  // ── Payload builders ─────────────────────────────────────────────────────

  buildScheduleData(): any[] {
    const endDate = this.isGoLive ? this.form.postGoLiveFWEndDate : this.form.fieldworkEndDate;
    const target = this.computeTargetPublicationDate(endDate);
    const base: Record<string, any> = {
      ReviewID: this.reviewIdNumber,
      ScheduleName: this.scheduleId,
      ServiceLine: this.form.serviceLines.join(', '),
      ParentChildType: this.form.parentChild,
      ParentReviewID: this.buildParentReviewId(),
      TargetPublicationDate: target ? this.formatIsoDate(target) : '',
      ScheduleStatus: 'Scheduled',
      CreatedBy: this.reviewerService.authService.getUserName() ?? '',
    };

    if (this.isGoLive) {
      const goLive = this.parseLocalDate(this.form.goLiveDate);
      const glrrInterimCallDate = goLive ? this.formatIsoDate(this.addWorkingDays(goLive, GLRR_FIELD_CONFIG.INTERIM_CALL_FROM_GO_LIVE)) : '';
      return [{
        ...base,
        GoLiveDate: this.form.goLiveDate,
        PreGoLiveFWStartDate: this.isPreAndPost ? this.form.preGoLiveFWStartDate : '',
        PreGoLiveFWEndDate: this.isPreAndPost ? this.form.preGoLiveFWEndDate : '',
        GLRRInterimCallDate: this.isPreAndPost ? glrrInterimCallDate : '',
        PostGoLiveFWStartDate: this.form.postGoLiveFWStartDate,
        PostGoLiveFWEndDate: this.form.postGoLiveFWEndDate,
      }];
    }

    return [{
      ...base,
      PlanFieldworkStartDate: this.form.fieldworkStartDate,
      PlanFieldworkEndDate: this.form.fieldworkEndDate,
    }];
  }

  buildTimelinePayload(): { TimeLine_Calculator: any[] } {
    const startDate = this.isGoLive ? this.form.postGoLiveFWStartDate : this.form.fieldworkStartDate;
    const endDate = this.isGoLive ? this.form.postGoLiveFWEndDate : this.form.fieldworkEndDate;
    const start = this.parseLocalDate(startDate);
    const end = this.parseLocalDate(endDate);
    const target = this.computeTargetPublicationDate(endDate);

    const fromStart = (workingDays: number): string =>
      start ? this.formatIsoDate(this.addWorkingDays(start, workingDays)) : '';
    const fromEnd = (workingDays: number): string =>
      end ? this.formatIsoDate(this.addWorkingDays(end, workingDays)) : '';

    return {
      TimeLine_Calculator: [{
        ReviewID: this.reviewIdNumber,
        ScheduleID: this.scheduleId,
        KickOffCallTargetDate: fromStart(TIMELINE_OFFSETS.KICK_OFF_CALL_FROM_START),
        RiskAssessmentTargetDate: fromStart(TIMELINE_OFFSETS.RISK_ASSESSMENT_FROM_START),
        PlanningCallEngagementLetterTargetDate: fromStart(TIMELINE_OFFSETS.PLANNING_CALL_ENGAGEMENT_LETTER_FROM_START),
        DocumentationReceivedTargetDate: fromStart(TIMELINE_OFFSETS.DOCUMENTATION_RECEIVED_FROM_START),
        FieldworkStartTargetDate: start ? this.formatIsoDate(start) : '',
        InterimMeetingOpsLeadTargetDate: fromStart(TIMELINE_OFFSETS.INTERIM_MEETING_OPS_LEAD_FROM_START),
        FieldworkEndTargetDate: end ? this.formatIsoDate(end) : '',
        PreClosingMeetingTargetDate: fromEnd(TIMELINE_OFFSETS.PRE_CLOSING_MEETING_FROM_END),
        DraftReportManagementResponseTargetDate: fromEnd(TIMELINE_OFFSETS.DRAFT_REPORT_MGMT_RESPONSE_FROM_END),
        ClosingMeetingTargetDate: fromEnd(TIMELINE_OFFSETS.CLOSING_MEETING_FROM_END),
        FinalReportIssuedActualDate: target ? this.formatIsoDate(target) : '',
        CreatedBy: this.reviewerService.authService.getUserName() ?? '',
      }],
    };
  }

  buildTimelinePayloadGLRR(): { TimeLine_CalculatorGLRR: any[] } {
    const goLive = this.parseLocalDate(this.form.goLiveDate);
    const postEnd = this.parseLocalDate(this.form.postGoLiveFWEndDate);
    const target = this.computeTargetPublicationDate(this.form.postGoLiveFWEndDate);

    const fromGoLive = (wd: number): string =>
      goLive ? this.formatIsoDate(this.addWorkingDays(goLive, wd)) : '';
    const fromPostEnd = (wd: number): string =>
      postEnd ? this.formatIsoDate(this.addWorkingDays(postEnd, wd)) : '';

    return {
      TimeLine_CalculatorGLRR: [{
        IdentifyProjectsForGLRRTargetDate: fromGoLive(GLRR_TIMELINE_OFFSETS.IDENTIFY_PROJECTS_FROM_GO_LIVE),
        InformationGatheringShareToMobTeamTargetDate: fromGoLive(GLRR_TIMELINE_OFFSETS.INFO_GATHERING_MOB_TEAM_FROM_GO_LIVE),
        IdentifyProjectsWaveForGLRRTargetDate: fromGoLive(GLRR_TIMELINE_OFFSETS.IDENTIFY_PROJECTS_WAVE_FROM_GO_LIVE),
        KickOffCallTargetDate: fromGoLive(GLRR_TIMELINE_OFFSETS.KICK_OFF_CALL_FROM_GO_LIVE),
        ObtainProcessDocumentationTargetDate: fromGoLive(GLRR_TIMELINE_OFFSETS.OBTAIN_PROCESS_DOCS_FROM_GO_LIVE),
        InterimMeetingTargetDate: fromGoLive(GLRR_TIMELINE_OFFSETS.INTERIM_MEETING_FROM_GO_LIVE),
        RemediateRepLetterAndObservationsTargetDate: fromGoLive(GLRR_TIMELINE_OFFSETS.REMEDIATE_REP_LETTER_FROM_GO_LIVE),
        PostGoLiveTestingStartTargetDate: fromGoLive(GLRR_TIMELINE_OFFSETS.POST_GO_LIVE_TESTING_START_FROM_GO_LIVE),
        PostGoLiveTestingEndTargetDate: fromGoLive(GLRR_TIMELINE_OFFSETS.POST_GO_LIVE_TESTING_END_FROM_GO_LIVE),
        ClosingMeetingTargetDate: fromPostEnd(GLRR_TIMELINE_OFFSETS.CLOSING_MEETING_FROM_POST_END),
        ProjectActionPlansReceivedTargetDate: fromPostEnd(GLRR_TIMELINE_OFFSETS.PROJECT_ACTION_PLANS_RECEIVED_FROM_POST_END),
        DraftReportManagerReviewTargetDate: fromPostEnd(GLRR_TIMELINE_OFFSETS.DRAFT_REPORT_MANAGER_REVIEW_FROM_POST_END),
        DraftReportGlobalLeadReviewTargetDate: fromPostEnd(GLRR_TIMELINE_OFFSETS.DRAFT_REPORT_GLOBAL_LEAD_REVIEW_FROM_POST_END),
        ReportPublicationDate: target ? this.formatIsoDate(target) : '',
      }],
    };
  }

  buildSubmitPayload(): { ScheduleData: any[]; TimeLine_Calculator: any[] } {
    return {
      ScheduleData: this.buildScheduleData(),
      TimeLine_Calculator: this.buildTimelinePayload().TimeLine_Calculator,
    };
  }

  private readonly scheduleFieldDataTypes: Record<string, string> = {
    ServiceLine: 'nvarchar',
    ParentChildType: 'nvarchar',
    ParentReviewID: 'int',
    GoLiveDate: 'datetime',
    PreGoLiveFWStartDate: 'datetime',
    PreGoLiveFWEndDate: 'datetime',
    GLRRInterimCallDate: 'datetime',
    PostGoLiveFWStartDate: 'datetime',
    PostGoLiveFWEndDate: 'datetime',
    TargetPublicationDate: 'datetime',
    PlanFieldworkStartDate: 'datetime',
    PlanFieldworkEndDate: 'datetime',
    ScheduleStatus: 'nvarchar',
  };

  buildSaveSchedulePayload(): any {
    const scheduleData = this.buildScheduleData()[0] ?? {};
    const { ReviewID, ScheduleName, CreatedBy, ...fieldEntries } = scheduleData;

    const fields = Object.entries(fieldEntries)
      .filter(([key, value]) => {
        if (value === null || value === undefined) return false;
        if (key === 'ServiceLine') return true;
        if (key === 'PreGoLiveFWStartDate' || key === 'PreGoLiveFWEndDate' || key === 'GLRRInterimCallDate') return this.isPreAndPost;
        if (this.scheduleFieldDataTypes[key] === 'datetime') return String(value) !== '';
        return true;
      })
      .map(([key, value]) => ({
        FieldName: key,
        FieldValue: String(value),
        DataType: this.scheduleFieldDataTypes[key] ?? 'nvarchar',
      }));

    let timelineObj: Record<string, any>;
    if (this.isGoLive) {
      timelineObj = this.buildTimelinePayloadGLRR().TimeLine_CalculatorGLRR[0] ?? {};
    } else {
      const { ReviewID: _rId, ScheduleID: _sId, CreatedBy: _cb, ...rest } = this.buildTimelinePayload().TimeLine_Calculator[0] ?? {};
      timelineObj = rest;
    }
    const dateCalcFields = Object.entries(timelineObj)
      .filter(([, value]) => value !== null && value !== undefined && String(value) !== '')
      .map(([key, value]) => ({ FieldName: key, FieldValue: String(value) }));

    return {
      category: 'SaveScheduleDynamic',
      ReviewID,
      ScheduleName,
      ReviewStatus: 'Scheduled',
      CreatedBy,
      Fields: fields,
      DateCalcFields: dateCalcFields,
    };
  }

  private buildParentReviewId(): number | null {
    if (this.form.parentChild === 'Parent') {
      return null;
    }
    return this.toReviewIdNumber(this.form.parentReviewId);
  }

  get isFwStarted(): boolean {
    const s = (this.reviewStatus ?? '').toLowerCase().replace(/[\s\-]+/g, '');
    if (!s) return false;
    // Standard/ODR/SPR: FW Started and all statuses that follow
    if (s === 'fwstarted' || s === 'fwcompleted') return true;
    // Reporting-phase statuses (shared by standard and Go-Live)
    if (s.startsWith('rptg') || s === 'completed') return true;
    // IM-Published variants
    if (s.startsWith('im')) return true;
    // Go-Live: POST-Go-Live FW Started and all statuses that follow
    if (s.startsWith('postgolive')) return true;
    return false;
  }
}
